import { BiDatasetList } from '@euler/model/bi/library';

/**
 * A real `GET /api/bi/datasets?section=builtin` response, captured from dev
 * rear (backend bi.datasets.api.smoke) on 2026-10-17 and cut to four
 * datasets. Enum values stay as the wire sends them. The orders `status`
 * and `order_type` value labels are as rear sends them since coded values
 * carry labels.
 */
export const kBiBuiltinDatasetsFixture = {
  section: 'builtin',
  datasets: [
    {
      kind: 'sql',
      catalog: {
        dataset: {
          kind: 'sql',
          model: 'orders',
        },
        label: {
          zh: '工单',
          en: 'Orders',
        },
        description: {
          zh: '每行一张接车工单（含子工单与已取消的工单），按开单时间统计。',
          en: 'One row per reception order, child and cancelled orders included; dated by when the order was opened.',
        },
        primaryTime: 'created_at',
        fields: [
          {
            key: 'created_at',
            label: {
              zh: '开单时间',
              en: 'Opened at',
            },
            role: 'dimension',
            type: 'datetime',
            grains: ['day', 'week', 'month', 'quarter', 'year'],
            conformedKey: 'date',
          },
          {
            key: 'started_at',
            label: {
              zh: '开始服务时间',
              en: 'Service started at',
            },
            role: 'dimension',
            type: 'datetime',
            grains: ['day', 'week', 'month', 'quarter', 'year'],
          },
          {
            key: 'finished_at',
            label: {
              zh: '完工时间',
              en: 'Finished at',
            },
            role: 'dimension',
            type: 'datetime',
            grains: ['day', 'week', 'month', 'quarter', 'year'],
          },
          {
            key: 'canceled_at',
            label: {
              zh: '取消时间',
              en: 'Cancelled at',
            },
            role: 'dimension',
            type: 'datetime',
            grains: ['day', 'week', 'month', 'quarter', 'year'],
          },
          {
            key: 'created_hour',
            label: {
              zh: '开单时段（小时）',
              en: 'Hour opened',
            },
            role: 'dimension',
            type: 'number',
          },
          {
            key: 'status',
            label: {
              zh: '工单状态',
              en: 'Status',
            },
            role: 'dimension',
            type: 'string',
            valueLabels: {
              pending: '待处理',
              inprogress: '进行中',
              cancelled: '已取消',
              finished: '已完成',
            },
          },
          {
            key: 'order_type',
            label: {
              zh: '工单类型',
              en: 'Order type',
            },
            role: 'dimension',
            type: 'string',
            valueLabels: {
              basic: '基础保养',
              repair: '维修保养',
              full: '全车保养',
            },
          },
          {
            key: 'service_type',
            label: {
              zh: '服务类型',
              en: 'Service type',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'service_agent',
            label: {
              zh: '服务顾问',
              en: 'Service advisor',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'service_agent_id',
            label: {
              zh: '服务顾问 ID',
              en: 'Service advisor ID',
            },
            role: 'dimension',
            type: 'number',
          },
          {
            key: 'plate',
            label: {
              zh: '车牌',
              en: 'Plate',
            },
            role: 'dimension',
            type: 'string',
            conformedKey: 'license_plate',
          },
          {
            key: 'vin',
            label: {
              zh: '车架号',
              en: 'VIN',
            },
            role: 'dimension',
            type: 'string',
            conformedKey: 'vin',
          },
          {
            key: 'vehicle_brand',
            label: {
              zh: '品牌',
              en: 'Brand',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'vehicle_model',
            label: {
              zh: '车型',
              en: 'Model',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'customer_mobile',
            label: {
              zh: '客户手机号',
              en: 'Customer phone',
            },
            role: 'dimension',
            type: 'string',
            conformedKey: 'customer_phone',
          },
          {
            key: 'first_visit',
            label: {
              zh: '首次到店',
              en: 'First visit',
            },
            role: 'dimension',
            type: 'boolean',
          },
          {
            key: 'reserved',
            label: {
              zh: '预约到店',
              en: 'Reserved',
            },
            role: 'dimension',
            type: 'boolean',
          },
          {
            key: 'main_order',
            label: {
              zh: '主工单',
              en: 'Main order',
            },
            role: 'dimension',
            type: 'boolean',
          },
          {
            key: 'order_count',
            label: {
              zh: '工单数',
              en: 'Orders',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'finished_count',
            label: {
              zh: '已完工数',
              en: 'Finished orders',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'cancelled_count',
            label: {
              zh: '已取消数',
              en: 'Cancelled orders',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'first_visit_count',
            label: {
              zh: '首次到店数',
              en: 'First visits',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'vehicle_count',
            label: {
              zh: '车辆数',
              en: 'Vehicles',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'turnaround',
            label: {
              zh: '交付时长',
              en: 'Turnaround',
            },
            role: 'measure',
            type: 'number',
            aggs: ['avg', 'min', 'max'],
            defaultAgg: 'avg',
            format: { kind: 'duration' },
          },
          {
            key: 'mileage',
            label: {
              zh: '进店里程',
              en: 'Mileage',
            },
            role: 'measure',
            type: 'number',
            aggs: ['avg', 'min', 'max'],
            defaultAgg: 'avg',
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'rating',
            label: {
              zh: '客户评分',
              en: 'Customer rating',
            },
            role: 'measure',
            type: 'number',
            aggs: ['avg', 'min', 'max'],
            defaultAgg: 'avg',
            format: { kind: 'number' },
          },
        ],
      },
    },
    {
      kind: 'sql',
      catalog: {
        dataset: {
          kind: 'sql',
          model: 'settlement_items',
        },
        label: {
          zh: '结算明细',
          en: 'Settlement items',
        },
        description: {
          zh: '每行一条结算项目，仅含每个工单最新一张结算单。金额单位为分，为折后小计；待定价项目不计入金额。整单优惠只在结算单上，不在此处。按单据状态筛选“已确认”即实收口径。',
          en: "One row per settlement line, from each task's newest settlement only. Amounts are line subtotals after line discounts, in cents; a price still to be decided has no amount. The form-level discount is not here. Filter status to confirmed for what was billed.",
        },
        primaryTime: 'created_at',
        fields: [
          {
            key: 'created_at',
            label: {
              zh: '结算时间',
              en: 'Settled at',
            },
            role: 'dimension',
            type: 'datetime',
            grains: ['day', 'week', 'month', 'quarter', 'year'],
            conformedKey: 'date',
          },
          {
            key: 'status',
            label: {
              zh: '单据状态',
              en: 'Form status',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'name',
            label: {
              zh: '项目',
              en: 'Item',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'kind',
            label: {
              zh: '类型（工时/材料）',
              en: 'Kind (labour / parts)',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'subject',
            label: {
              zh: '服务主题',
              en: 'Subject',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'material_type',
            label: {
              zh: '材料类型',
              en: 'Part type',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'brand',
            label: {
              zh: '材料品牌',
              en: 'Part brand',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'plate',
            label: {
              zh: '车牌',
              en: 'Plate',
            },
            role: 'dimension',
            type: 'string',
            conformedKey: 'license_plate',
          },
          {
            key: 'vin',
            label: {
              zh: '车架号',
              en: 'VIN',
            },
            role: 'dimension',
            type: 'string',
            conformedKey: 'vin',
          },
          {
            key: 'service_agent',
            label: {
              zh: '服务顾问',
              en: 'Service advisor',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'item_count',
            label: {
              zh: '项目行数',
              en: 'Lines',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'form_count',
            label: {
              zh: '结算单数',
              en: 'Settlements',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'task_count',
            label: {
              zh: '工单数',
              en: 'Tasks',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'quantity',
            label: {
              zh: '数量',
              en: 'Quantity',
            },
            role: 'measure',
            type: 'number',
            aggs: ['sum', 'avg'],
            defaultAgg: 'sum',
            format: { kind: 'number' },
          },
          {
            key: 'unit_price',
            label: {
              zh: '单价',
              en: 'Unit price',
            },
            role: 'measure',
            type: 'number',
            aggs: ['avg', 'min', 'max'],
            defaultAgg: 'avg',
            format: { kind: 'currency' },
          },
          {
            key: 'amount',
            label: {
              zh: '金额',
              en: 'Amount',
            },
            role: 'measure',
            type: 'number',
            aggs: ['sum', 'avg', 'min', 'max'],
            defaultAgg: 'sum',
            format: { kind: 'currency' },
          },
        ],
      },
    },
    {
      kind: 'sql',
      catalog: {
        dataset: {
          kind: 'sql',
          model: 'leads',
        },
        label: {
          zh: '销售线索',
          en: 'Leads',
        },
        description: {
          zh: '每行一条销售线索，仅含当前用户在线索列表中可见的线索。',
          en: 'One row per sales lead, limited to the leads the caller can see in the leads list.',
        },
        primaryTime: 'created_at',
        fields: [
          {
            key: 'created_at',
            label: {
              zh: '创建时间',
              en: 'Created at',
            },
            role: 'dimension',
            type: 'datetime',
            grains: ['day', 'week', 'month', 'quarter', 'year'],
            conformedKey: 'date',
          },
          {
            key: 'status',
            label: {
              zh: '状态',
              en: 'Status',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'is_open',
            label: {
              zh: '跟进中',
              en: 'Open',
            },
            role: 'dimension',
            type: 'boolean',
          },
          {
            key: 'intention',
            label: {
              zh: '意向',
              en: 'Intention',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'type',
            label: {
              zh: '类型',
              en: 'Type',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'source',
            label: {
              zh: '来源',
              en: 'Source',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'origin',
            label: {
              zh: '产生方式',
              en: 'Origin',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'visibility',
            label: {
              zh: '可见范围',
              en: 'Visibility',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'assignee_name',
            label: {
              zh: '负责人',
              en: 'Owner',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'created_by_name',
            label: {
              zh: '创建人',
              en: 'Created by',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'plate',
            label: {
              zh: '车牌',
              en: 'Plate',
            },
            role: 'dimension',
            type: 'string',
            conformedKey: 'license_plate',
          },
          {
            key: 'vin',
            label: {
              zh: '车架号',
              en: 'VIN',
            },
            role: 'dimension',
            type: 'string',
            conformedKey: 'vin',
          },
          {
            key: 'customer_name',
            label: {
              zh: '客户姓名',
              en: 'Customer name',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'customer_phone',
            label: {
              zh: '客户电话',
              en: 'Customer phone',
            },
            role: 'dimension',
            type: 'string',
            conformedKey: 'customer_phone',
          },
          {
            key: 'vehicle_brand',
            label: {
              zh: '品牌',
              en: 'Brand',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'target_service_date',
            label: {
              zh: '目标服务日期',
              en: 'Target service date',
            },
            role: 'dimension',
            type: 'date',
            grains: ['day', 'week', 'month', 'quarter', 'year'],
          },
          {
            key: 'expiry_date',
            label: {
              zh: '过期日期',
              en: 'Expiry date',
            },
            role: 'dimension',
            type: 'date',
            grains: ['day', 'week', 'month', 'quarter', 'year'],
          },
          {
            key: 'last_activity_at',
            label: {
              zh: '最近跟进时间',
              en: 'Last activity at',
            },
            role: 'dimension',
            type: 'datetime',
            grains: ['day', 'week', 'month', 'quarter', 'year'],
          },
          {
            key: 'lead_count',
            label: {
              zh: '线索数',
              en: 'Leads',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'open_count',
            label: {
              zh: '跟进中线索数',
              en: 'Open leads',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'converted_count',
            label: {
              zh: '已转化线索数',
              en: 'Converted leads',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'overdue_outreach_count',
            label: {
              zh: '外呼已到期线索数',
              en: 'Outreach due leads',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'stale_count',
            label: {
              zh: '超过14天未跟进线索数',
              en: 'Leads idle over 14 days',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
        ],
      },
    },
    {
      kind: 'sql',
      catalog: {
        dataset: {
          kind: 'sql',
          model: 'reservations',
        },
        label: {
          zh: '预约',
          en: 'Reservations',
        },
        description: {
          zh: '每行一条预约（不含已删除）。',
          en: 'One row per reservation, deleted ones excluded.',
        },
        primaryTime: 'created_at',
        fields: [
          {
            key: 'created_at',
            label: {
              zh: '创建时间',
              en: 'Created at',
            },
            role: 'dimension',
            type: 'datetime',
            grains: ['day', 'week', 'month', 'quarter', 'year'],
            conformedKey: 'date',
          },
          {
            key: 'expected_arrival_at',
            label: {
              zh: '预计到店时间',
              en: 'Expected arrival',
            },
            role: 'dimension',
            type: 'datetime',
            grains: ['day', 'week', 'month', 'quarter', 'year'],
          },
          {
            key: 'status',
            label: {
              zh: '状态',
              en: 'Status',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'is_open',
            label: {
              zh: '待到店',
              en: 'Open',
            },
            role: 'dimension',
            type: 'boolean',
          },
          {
            key: 'from_lead',
            label: {
              zh: '来自线索',
              en: 'From a lead',
            },
            role: 'dimension',
            type: 'boolean',
          },
          {
            key: 'has_order',
            label: {
              zh: '已开单',
              en: 'Has an order',
            },
            role: 'dimension',
            type: 'boolean',
          },
          {
            key: 'service_agent_name',
            label: {
              zh: '服务顾问',
              en: 'Service advisor',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'has_service_agent',
            label: {
              zh: '已分配顾问',
              en: 'Advisor assigned',
            },
            role: 'dimension',
            type: 'boolean',
          },
          {
            key: 'created_by_name',
            label: {
              zh: '创建人',
              en: 'Created by',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'customer_type',
            label: {
              zh: '客户类型',
              en: 'Customer type',
            },
            role: 'dimension',
            type: 'number',
          },
          {
            key: 'plate',
            label: {
              zh: '车牌',
              en: 'Plate',
            },
            role: 'dimension',
            type: 'string',
            conformedKey: 'license_plate',
          },
          {
            key: 'vin',
            label: {
              zh: '车架号',
              en: 'VIN',
            },
            role: 'dimension',
            type: 'string',
            conformedKey: 'vin',
          },
          {
            key: 'customer_name',
            label: {
              zh: '客户姓名',
              en: 'Customer name',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'customer_phone',
            label: {
              zh: '客户电话',
              en: 'Customer phone',
            },
            role: 'dimension',
            type: 'string',
            conformedKey: 'customer_phone',
          },
          {
            key: 'vehicle_brand',
            label: {
              zh: '品牌',
              en: 'Brand',
            },
            role: 'dimension',
            type: 'string',
          },
          {
            key: 'reservation_count',
            label: {
              zh: '预约数',
              en: 'Reservations',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'open_count',
            label: {
              zh: '待到店预约数',
              en: 'Open reservations',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'arrived_count',
            label: {
              zh: '已到店预约数',
              en: 'Arrived',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'converted_count',
            label: {
              zh: '已开单预约数',
              en: 'Converted',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'showed_up_count',
            label: {
              zh: '到店预约数',
              en: 'Showed up',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'no_show_count',
            label: {
              zh: '爽约预约数',
              en: 'No-shows',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'cancelled_count',
            label: {
              zh: '已取消预约数',
              en: 'Cancelled',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
          {
            key: 'unassigned_open_count',
            label: {
              zh: '未分配顾问的待到店预约数',
              en: 'Open, no advisor',
            },
            role: 'measure',
            type: 'number',
            aggs: [],
            format: { kind: 'number', decimals: 0 },
          },
        ],
      },
    },
  ],
} as BiDatasetList;
